import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CloseIcon,
  CskhHeaderIcon,
  ModelTierIcon,
  NewChatbotMenuIcon,
  NewChatIcon,
  NewIconChatbot,
  PlanArrowDownIcon,
  TokenEnergyIcon,
  TokenRedIcon,
  WalletThunderIcon,
} from '@assets/icon';
import { Dropdown } from '@components/Dropdown';
import { ChatAgentType, DEFAULT_MODEL_TIER, type ModelTier } from '@constants/chat-agents';
import {
  getAiWalletBalance,
  getModelTiers,
  setSocketModelTier,
  type AiWalletBalanceItem,
  type ModelTierOption,
  type ModelTierWalletCode,
} from '@services/chatService';
import './ChatHeader.scss';

interface ChatHeaderProps {
  onClose?: () => void;
  onMenuClick?: () => void;
  onNewChatClick?: () => void;
  showNewChat?: boolean;
  isAudioEnabled?: boolean;
  onToggleAudio?: () => void;
  isBotSpeaking?: boolean;
  onClickSetting?: () => void;
  activeAgent?: ChatAgentType;
  /** Trả về false khi việc chuyển agent bị hoãn (đang countdown / chờ confirm) → header giữ nguyên */
  onAgentChange?: (agent: ChatAgentType) => boolean | void;
}

const SUPPORT_PLAN_KEY = 'Support';
const SUPPORT_PLAN = {
  label: 'Chăm sóc khách hàng',
  triggerLabel: 'CSKH',
  description: 'Hỗ trợ 24/7',
};

// Dùng khi API model-tiers lỗi: vẫn cho chọn gói, chỉ không hiện khoảng token
const FALLBACK_MODEL_TIERS: ModelTierOption[] = [
  { code: 'BASIC', name: 'Cơ bản', description: 'Phản hồi nhanh', minTokenCost: 0, maxTokenCost: 0, walletCode: 'AI_ENERGY' },
  { code: 'PRO', name: 'Tiêu chuẩn', description: 'Hỗ trợ toàn diện', minTokenCost: 0, maxTokenCost: 0, walletCode: 'AI_TOKEN' },
  { code: 'MAX', name: 'Nâng cao', description: 'Hỗ trợ nâng cao', minTokenCost: 0, maxTokenCost: 0, walletCode: 'AI_TOKEN' },
];

// Thứ tự hiển thị số dư trên banner: token vàng trước, token đỏ sau
const WALLET_DISPLAY_ORDER: ModelTierWalletCode[] = ['AI_ENERGY', 'AI_TOKEN'];

// AI_ENERGY = token vàng (gói Cơ bản), AI_TOKEN = token đỏ (gói Tiêu chuẩn, Nâng cao)
const TokenIcon: React.FC<{ walletCode: ModelTierWalletCode }> = ({ walletCode }) => (
  <span className="flex h-3 w-3 shrink-0 items-center [&>svg]:h-3 [&>svg]:w-3" aria-hidden>
    {walletCode === 'AI_ENERGY' ? <TokenEnergyIcon /> : <TokenRedIcon />}
  </span>
);

const formatTokenRange = (tier: ModelTierOption): string | null => {
  if (!tier.maxTokenCost) return null;
  return tier.minTokenCost === tier.maxTokenCost
    ? String(tier.maxTokenCost)
    : `${tier.minTokenCost}~${tier.maxTokenCost}`;
};

const formatBalance = (value: number): string =>
  new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 2 }).format(Number(value) || 0);

const SelectedMarker: React.FC<{ isSelected: boolean; isSupport?: boolean }> = ({
  isSelected,
  isSupport = false,
}) => (
  <span className="flex w-8 shrink-0 justify-center pt-1">
    {isSelected && (
      isSupport ? (
        <span className="flex h-5 w-5 items-center justify-center [&>svg]:h-5 [&>svg]:w-5">
          <CskhHeaderIcon />
        </span>
      ) : (
        <span className="flex h-[11.14px] items-center [&>svg]:h-[11.14px] [&>svg]:w-[19px]">
          <NewIconChatbot />
        </span>
      )
    )}
  </span>
);

const ChatHeader: React.FC<ChatHeaderProps> = ({
  onClose,
  onMenuClick,
  onNewChatClick,
  showNewChat = true,
  activeAgent = ChatAgentType.AI,
  onAgentChange,
}) => {
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState<ModelTier | typeof SUPPORT_PLAN_KEY>(
    activeAgent === ChatAgentType.CSKH ? SUPPORT_PLAN_KEY : DEFAULT_MODEL_TIER,
  );
  const [planDropdownOpen, setPlanDropdownOpen] = useState(false);
  const [modelTiers, setModelTiers] = useState<ModelTierOption[]>([]);
  const [wallets, setWallets] = useState<AiWalletBalanceItem[]>([]);
  const bannerWallets = WALLET_DISPLAY_ORDER
    .map((code) => wallets.find((wallet) => wallet.walletCode === code))
    .filter((wallet): wallet is AiWalletBalanceItem => !!wallet);
  const tierOptions = modelTiers.length > 0 ? modelTiers : FALLBACK_MODEL_TIERS;
  const selectedTier = tierOptions.find((tier) => tier.code === selectedPlan);
  const selectedPlanLabel = selectedPlan === SUPPORT_PLAN_KEY
    ? SUPPORT_PLAN.triggerLabel
    : selectedTier?.name ?? tierOptions[0].name;

  const loadModelTiers = useCallback(async () => {
    const token = sessionStorage.getItem('access_token');
    if (!token) return;
    try {
      const tiers = await getModelTiers(token);
      if (tiers.length > 0) setModelTiers(tiers);
    } catch (error) {
      console.error('Error fetching model tiers:', error);
    }
  }, []);

  useEffect(() => {
    void loadModelTiers();
  }, [loadModelTiers]);

  // Token có thể chưa sẵn lúc mount → mở Action Sheet mà chưa có dữ liệu thì gọi lại
  useEffect(() => {
    if (planDropdownOpen && modelTiers.length === 0) {
      void loadModelTiers();
    }
  }, [planDropdownOpen, modelTiers.length, loadModelTiers]);

  // Số dư thay đổi sau mỗi lượt chat → lấy lại mỗi lần mở Action Sheet
  useEffect(() => {
    if (!planDropdownOpen) return;
    const token = sessionStorage.getItem('access_token');
    if (!token) return;
    getAiWalletBalance(token)
      .then(setWallets)
      .catch((error) => console.error('Error fetching AI wallet balance:', error));
  }, [planDropdownOpen]);

  // Gói đang chọn → modelTier gửi kèm mọi tin nhắn socket
  useEffect(() => {
    setSocketModelTier(selectedPlan === SUPPORT_PLAN_KEY ? null : selectedPlan);
  }, [selectedPlan]);

  // Keep plan trigger in sync when agent is changed elsewhere (e.g. AgentSelector)
  useEffect(() => {
    if (activeAgent === ChatAgentType.CSKH) {
      setSelectedPlan(SUPPORT_PLAN_KEY);
      return;
    }
    setSelectedPlan((prev) => (prev === SUPPORT_PLAN_KEY ? DEFAULT_MODEL_TIER : prev));
  }, [activeAgent]);

  const handleSelectPlan = (planKey: ModelTier | typeof SUPPORT_PLAN_KEY) => {
    setPlanDropdownOpen(false);

    // Chuyển agent bị hoãn (đang countdown / chờ confirm) → chưa đổi header,
    // agent đổi thật sẽ đồng bộ lại qua effect theo activeAgent
    const targetAgent = planKey === SUPPORT_PLAN_KEY ? ChatAgentType.CSKH : ChatAgentType.AI;
    const needsAgentChange = planKey === SUPPORT_PLAN_KEY
      ? activeAgent !== ChatAgentType.CSKH
      : activeAgent === ChatAgentType.CSKH;
    if (needsAgentChange && onAgentChange?.(targetAgent) === false) return;

    setSelectedPlan(planKey);
  };

  return (
    <div className="chat-header">
      <div className="chat-header-left">
        <button
          className="flex h-10 w-10 items-center justify-center gap-2.5 rounded-[50px] border-0 bg-[#FFFFFF80] p-2 text-[var(--colour-primary-neutral-shade-md)] opacity-100 shadow-[0px_2px_16px_0px_#0000000F] transition-all hover:bg-[rgba(15,23,42,0.06)]"
          type="button"
          onClick={onMenuClick}
          aria-label={t('common.menu', 'Menu')}
          data-tooltip-id="chatbot-tooltip"
          data-tooltip-content="Menu"
          data-tooltip-place="top"
        >
          <NewChatbotMenuIcon />
        </button>
        <Dropdown
          open={planDropdownOpen}
          onOpenChange={setPlanDropdownOpen}
          forceRender
          destroyPopupOnHide={false}
          trigger={['click']}
          placement="bottomLeft"
          items={[]}
          dropdownRender={() => (
            <div className="flex w-[306px] flex-col gap-3 overflow-hidden backdrop-saturate-150 rounded-[24px] border border-white bg-white/90 p-3 shadow-[0px_8px_40px_0px_#0000001F] backdrop-blur-lg">
              {bannerWallets.length > 0 && (
                <div className="flex items-center gap-2 rounded-[16px] border border-[#FFE1E1] bg-[#FFF5F5] px-2 py-2 font-sans text-sm leading-5 text-[#242428]">
                  <span className="flex h-5 w-5 shrink-0 items-center [&>svg]:h-5 [&>svg]:w-5" aria-hidden>
                    <WalletThunderIcon />
                  </span>
                  <span className="flex flex-wrap items-center gap-x-1">
                    <span>Còn lại:</span>
                    {bannerWallets.map((wallet, index) => (
                      <span key={wallet.walletCode} className="flex items-center gap-1">
                        <span className="font-semibold">{formatBalance(wallet.available)}</span>
                        <span>token</span>
                        <TokenIcon walletCode={wallet.walletCode} />
                        {index < bannerWallets.length - 1 && <span>,</span>}
                      </span>
                    ))}
                  </span>
                </div>
              )}
              {tierOptions.map((tier) => {
                const tokenRange = formatTokenRange(tier);
                return (
                  <button
                    key={tier.code}
                    type="button"
                    className="flex w-full items-start gap-2 rounded-[18px] border-0 bg-transparent px-1 py-1 text-left transition-colors hover:bg-black/[0.03]"
                    onClick={() => handleSelectPlan(tier.code)}
                  >
                    <SelectedMarker isSelected={selectedPlan === tier.code} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="font-sans text-base font-normal leading-6 text-[#242429]">
                          {tier.name}
                        </span>
                        <span className="truncate font-sans text-xs font-normal leading-4 text-[#94949A]">
                          {tier.description}
                        </span>
                      </span>
                      {tokenRange && (
                        <span className="mt-1 flex items-center gap-1 font-sans text-xs font-normal leading-4 text-[#242428]">
                          <span className="flex h-3 w-3 shrink-0 items-center [&>svg]:h-3 [&>svg]:w-3" aria-hidden>
                            <ModelTierIcon />
                          </span>
                          {tokenRange}
                          <TokenIcon walletCode={tier.walletCode} />
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
              <div className="h-px w-full shrink-0 bg-[#E5E5EA]" role="separator" />
              <button
                type="button"
                className="flex w-full items-start gap-2 rounded-[18px] border-0 bg-transparent px-1 py-1 text-left transition-colors hover:bg-black/[0.03]"
                onClick={() => handleSelectPlan(SUPPORT_PLAN_KEY)}
              >
                <SelectedMarker isSelected={selectedPlan === SUPPORT_PLAN_KEY} isSupport />
                <span className="min-w-0 flex-1">
                  <span className="block font-sans text-base font-normal leading-6 text-[#242429]">
                    {SUPPORT_PLAN.label}
                  </span>
                  <span className="mt-1 block font-sans text-xs font-normal leading-4 text-[#94949A]">
                    {SUPPORT_PLAN.description}
                  </span>
                </span>
              </button>
            </div>
          )}
        >
          <button
            type="button"
            className="flex items-center gap-2 rounded-[50px] border-0 bg-transparent p-0"
            aria-label="Trợ lý"
            data-tooltip-id="chatbot-tooltip"
            data-tooltip-content="Trợ lý"
            data-tooltip-place="top"
          >
            {selectedPlan === SUPPORT_PLAN_KEY ? (
              <span className="flex h-8 w-8 items-center justify-center [&>svg]:h-8 [&>svg]:w-8">
                <CskhHeaderIcon />
              </span>
            ) : (
              <span className="flex h-[18.8px] items-center [&>svg]:h-[18.8px] [&>svg]:w-8">
                <NewIconChatbot />
              </span>
            )}
            {selectedPlan !== SUPPORT_PLAN_KEY && (
              <span className="font-sans text-base font-normal leading-6 text-[#242428]">
                Tendo AI
              </span>
            )}
            <span
              className={
                selectedPlan === SUPPORT_PLAN_KEY
                  ? 'align-middle font-[Roboto] text-base font-normal leading-6 tracking-normal text-[#242428]'
                  : 'font-sans text-base font-normal leading-6 text-[#94949E]'
              }
            >
              {selectedPlanLabel}
            </span>
            <span className="flex h-5 w-5 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
              <PlanArrowDownIcon />
            </span>
          </button>
        </Dropdown>
        {/*
          Tạm ẩn mute/setting theo thiết kế mới.
          Giữ lại props và logic liên quan để dễ bật lại trong tương lai.
          <button
            className={getSpeakerClassName()}
            onClick={onToggleAudio}
            data-tooltip-id="chatbot-tooltip"
            data-tooltip-content={isAudioEnabled ? t('common.mute', 'Tắt âm thanh') : t('common.unmute', 'Bật âm thanh')}
          >
            {isAudioEnabled ? <VolumeIcon /> : <VolumeMuteIcon />}
          </button>
          <button
            className="chat-header-icon-btn ml-2"
            onClick={onClickSetting}
            data-tooltip-id="chatbot-tooltip"
            data-tooltip-content={t('common.setting', 'Cài đặt')}
          >
            <SettingIcon />
          </button>
        */}
      </div>
      <div className="chat-header-right">
        <div className="flex h-10 items-center gap-1 rounded-[50px] border-0 bg-[#FFFFFF80] p-1 opacity-100 shadow-[0px_2px_16px_0px_#0000000F]">
          {showNewChat && (
            <button
              className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent text-[var(--colour-primary-neutral-shade-md)] transition-all hover:bg-[rgba(15,23,42,0.06)] [&>svg]:h-5 [&>svg]:w-5"
              type="button"
              onClick={onNewChatClick}
              aria-label="Cuộc trò chuyện mới"
              data-tooltip-id="chatbot-tooltip"
              data-tooltip-content="Cuộc trò chuyện mới"
              data-tooltip-place="top"
            >
              <NewChatIcon />
            </button>
          )}
          <button
            className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent opacity-100 transition-all hover:bg-[rgba(15,23,42,0.06)] [&>svg]:h-[14px] [&>svg]:w-[14px] [&>svg_path]:stroke-[#242428] [&>svg_path]:stroke-[1.5px]"
            onClick={onClose}
            type="button"
            data-tooltip-id="chatbot-tooltip"
            data-tooltip-content="Thoát Tendoo AI"
            data-tooltip-place="top"
          >
            <CloseIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatHeader;
